import { showUserAlert } from "../utils/userErrorMessage";
import { fetchAllRows } from "../shared/fetchAllRows";
// src/pages/MonthlyClosing.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,PieChart, Pie, Legend, Cell 
} from "recharts";

// ----- 헬퍼: 월 경계 -----
const monthBounds = (yyyyMM /* '2025-09' */) => {
  const [y,m] = yyyyMM.split("-").map(Number);
  const start = new Date(Date.UTC(y, m-1, 1, 0, 0, 0));
  const end = new Date(Date.UTC(y, m, 1, 0, 0, 0)); // [start, end)
  const pad = (n)=> String(n).padStart(2,"0");
  return {
    from: `${start.getUTCFullYear()}-${pad(start.getUTCMonth()+1)}-${pad(start.getUTCDate())}`,
    toExcl: `${end.getUTCFullYear()}-${pad(end.getUTCMonth()+1)}-${pad(end.getUTCDate())}`, // exclusive
    toIncl: new Date(end.getTime()-86400000).toISOString().slice(0,10), // for 화면 표기
    daysInMonth: Math.round((end-start)/86400000),
  };
};
const prevMonthStr = (yyyyMM) => {
  const [y,m] = yyyyMM.split("-").map(Number);
  const d = new Date(Date.UTC(y, m-1, 1));
  d.setUTCMonth(d.getUTCMonth()-1);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}`;
};
const yearAgoStr = (yyyyMM) => {
  const [y, m] = yyyyMM.split("-").map(Number);
  return `${y - 1}-${String(m).padStart(2, "0")}`;
};


const EXPENSE = {
  fuel: ["jooyu"],                      // 유류
  tolls: ["tonghaeng"],                 // 통행료
  labor: ["johab","geubyeo","4bohum","sooksic"], // 인건비
  vehicle: ["halbu"],                   // 차량 가격(할부/리스)
  // 기타 필요시 추가
};
const number = (v) => (Number(v)||0).toLocaleString();

// 파일 상단 유틸 근처에 추가
const monthToDate = (yyyyMM) => {
  const [y, m] = yyyyMM.split("-");
  return `${y}-${m}-01`;
};

const renderAiSummary = (text) => {
  const source = text || "버튼을 눌러 요약을 생성하세요.";
  return source.split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};





  // 원형 그래프 카테고리 묶음
  const PIE_GROUPS = {
    "인건비": ["geubyeo", "4bohum"],
    "유류비": ["jooyu"],
    "정비비": ["jungbi"],
    "할부":   ["halbu"],
    "보험료": ["bohum"],
    "운영비용": ["tonghaeng", "joocha", "sooksic", "secha"],
    "제세공과금": ["gwatae", "johab", "unyoung", "gps"],
    "기타": ["gita"],
  };
  // 카테고리 고정 순서(PIE_GROUPS 키 순서와 같게)
  const PIE_ORDER = Object.keys(PIE_GROUPS);

  // 색 팔레트(원하는 색으로 교체 가능)
  const PIE_COLORS = [
    "#2563eb", // 인건비
    "#16a34a", // 유류비
    "#f59e0b", // 정비비
    "#ef4444", // 할부
    "#8b5cf6", // 보험료
    "#06b6d4", // 운영비용
    "#f97316", // 제세공과금
    "#64748b", // 기타
  ];

  

  // 유틸(파일 상단 유틸 근처)
  const norm = (s) =>
    String(s ?? "")
      .normalize("NFKC")
      .toUpperCase()
      .replace(/\s+/g, "")     // 공백 전부 제거
      .replace(/[-_.]/g, "")   // -, _, . 제거
      .trim();

  const last4 = (s) => norm(s).slice(-4);
  const isFourDigitCar = (s) => /^[0-9]{4}$/.test(String(s ?? "").trim());  

  // 카테고리 이름 -> 색
  const colorFor = (name) => {
    const idx = Math.max(0, PIE_ORDER.indexOf(name));
    return PIE_COLORS[idx % PIE_COLORS.length];
  };

  const toPieData = (rows) => {
    const sumKeys = (row, keys) => keys.reduce((a, k) => a + (Number(row[k]) || 0), 0);
    const box = Object.fromEntries(Object.keys(PIE_GROUPS).map((k) => [k, 0]));
    for (const e of rows || []) {
      for (const [label, keys] of Object.entries(PIE_GROUPS)) {
        box[label] += sumKeys(e, keys);
      }
    }
    // 0 값은 감춤
    return Object.entries(box)
      .map(([name, value]) => ({ name, value }))
      .filter((d) => d.value > 0);
  };


  const weeklyAggregate = (rows, monthStr) => {
    const { from } = monthBounds(monthStr);
    const start = new Date(from + "T00:00:00Z");
    const weekKey = (dStr) => {
      const d = new Date(dStr + "T00:00:00Z");
      const dayOffset = Math.floor((d - start) / 86400000);
      const wk = Math.floor(dayOffset / 7) + 1;
      return `${wk}주`;
    };
    const byWeek = {};
    for (const r of rows) {
      if (!r.date) continue;
      const k = weekKey(r.date);
      byWeek[k] = (byWeek[k] || 0) + (Number(r.money) || 0);
    }
    // 정렬된 [{week, amount}]
    return Object.entries(byWeek)
      .sort((a,b) => Number(a[0].replace("주","")) - Number(b[0].replace("주","")))
      .map(([week, amount]) => ({ week, amount }));
  };
  


export default function MonthlyClosing() {
  const loadGeneration = useRef(0);
  // 기본값: 지난달
  const now = new Date();
  const defaultMonth = (()=> {
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth()-1, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}`;
  })();

  const [month, setMonth] = useState(defaultMonth);
  const [loading, setLoading] = useState(false);

  const company = localStorage.getItem("email") || "";

  // 결과 상태
  const [reservations, setReservations] = useState([]);       // 이번달
  const [reservationsPrev, setReservationsPrev] = useState([]); // 전월
  const [reservationsYearAgo, setReservationsYearAgo] = useState([]); // 👈 작년 동월
  const [expenses, setExpenses] = useState([]);               // 이번달 cars-expenses
  const [expensesPrevYear, setExpensesPrevYear] = useState([]); // 전년 동월 cars-expenses
  const [safety, setSafety] = useState([]);                   // 이번달 safety
  const [aiLocked, setAiLocked] = useState(false);

  const [allowedCars, setAllowedCars] = useState(null);



  // cars.direct=true 여부로 포함 판단
  const isAllowedCar = useMemo(() => {
    return (carNum) => {
      // ✅ allowedCars 로딩 전에는 필터로 날리지 말고 통과
      if (!allowedCars) return true;

      const n = norm(carNum);
      if (!n) return false;
      const okFull = allowedCars.full.has(n);
      const okLast4 = allowedCars.last4.has(last4(n));

      return okFull || okLast4;
    };
  }, [allowedCars]);

  // reservations / expenses를 allowed 차량만 남기기
  const reservationsF         = useMemo(() => reservations.filter(r => isAllowedCar(r.car_number)), [reservations, isAllowedCar]);
  const reservationsPrevF     = useMemo(() => reservationsPrev.filter(r => isAllowedCar(r.car_number)), [reservationsPrev, isAllowedCar]);
  const reservationsYearAgoF  = useMemo(() => reservationsYearAgo.filter(r => isAllowedCar(r.car_number)), [reservationsYearAgo, isAllowedCar]);
  const expensesF             = useMemo(() => expenses.filter(e => isAllowedCar(e.car_number)), [expenses, isAllowedCar]);
  const expensesPrevYearF     = useMemo(() => expensesPrevYear.filter(e => isAllowedCar(e.car_number)), [expensesPrevYear, isAllowedCar]);

  // 메모된 데이터셋
  const pieThisMonth = useMemo(() => toPieData(expensesF),         [expensesF]);
  const pieYearAgo   = useMemo(() => toPieData(expensesPrevYearF), [expensesPrevYearF]);

  // 컴포넌트 내부에 추가
  const loadSavedAI = async () => {
    if (!company) return;
    const monthDate = monthToDate(month);
    const { data, error } = await supabase
      .from("ai_data")
      .select("ai")
      .eq("company", company)
      .eq("date", monthDate)
      .maybeSingle();

    if (!error && data?.ai) {
      setAiMsg(data.ai); // 저장된 요약을 에디터/화면에 표시
      setAiLocked(true);          // ✅ 이미 있음 → 이번 달 락
    } else {
      setAiMsg("");               // 기존 표시 지우기(선택)
      setAiLocked(false);         // ✅ 없음 → 생성 가능
    }
  };

  const saveAiSummary = async (summaryText) => {
    if (!company) return;
    const monthDate = monthToDate(month);

    const { error } = await supabase
      .from("ai_data")
      .upsert(
        { company, date: monthDate, ai: summaryText },
        { onConflict: "company,date" } // (company,date) 유니크 키 권장!
      );

    if (error) {
      showUserAlert("AI 요약 저장 실패: " + error.message);
    } else {
      setAiLocked(true); // ✅ 저장 성공 시 이번 달 락
    }
  };

  useEffect(() => {
    loadSavedAI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, company]);

  



  // ----------------- 데이터 로드 -----------------
  const fetchAll = useCallback(async () => {
    const generation = ++loadGeneration.current;
    const current = () => generation === loadGeneration.current;
    if (!company) { showUserAlert("회사 정보가 없습니다. 다시 로그인해주세요."); return; }
    setLoading(true);
    try {
      const { from, toExcl } = monthBounds(month);
      const prev = prevMonthStr(month);
      const { from: pf, toExcl: pt } = monthBounds(prev);
      const yearAgo = yearAgoStr(month);
      const { from: yf, toExcl: yt } = monthBounds(yearAgo);

      // ✅ 0) cars: direct=true
      const C = await fetchAllRows(() => supabase.from("cars")
        .select("car_number, car_number_only, direct").eq("company", company).eq("direct", true).order("id"));
      if (!current()) return;

      const full = new Set(), l4 = new Set();
      (C || []).forEach(c => {
        if (c.car_number) {
          const carNumber = norm(c.car_number);
          full.add(carNumber);
          l4.add(last4(carNumber));
        }
        if (c.car_number_only) {
          const k = norm(c.car_number_only);
          l4.add(k);
          if (k.length === 4) l4.add(k);           // 4자리 그대로
          else l4.add(k.slice(-4));                // 혹시 길면 뒤4자리도
        }
      });


      // 상태로도 보관(다른 곳에서 쓸 수 있게)
      setAllowedCars({ full, last4: l4 });

      // 로컬 허용 함수 (이번 fetchAll 안에서 바로 필터에 사용)
      const isAllowedCarLocal = (carNum) => {
        const n = norm(carNum);
        if (!n) return false;
        return full.has(n) || l4.has(last4(n)); // ✅ last4()로 통일
      };

      // 1) reservations (이번달 + 전월 + 작년 동월)
      const loadPeriod = (table, start, end) => fetchAllRows(() => supabase.from(table)
        .select("*").eq("company", company).gte("date", start).lt("date", end).order("id"));
      const [R, Rp, Ry, E, EY, S] = await Promise.all([
        loadPeriod("reservations", from, toExcl), loadPeriod("reservations", pf, pt),
        loadPeriod("reservations", yf, yt), loadPeriod("cars-expenses", from, toExcl),
        loadPeriod("cars-expenses", yf, yt), loadPeriod("safety", from, toExcl),
      ]);
      if (!current()) return;

      // ✅ reservations 즉시 필터
      const Rf  = (R  || []).filter(r  => isAllowedCarLocal(r.car_number));
      const Rpf = (Rp || []).filter(rp => isAllowedCarLocal(rp.car_number));
      const Ryf = (Ry || []).filter(ry => isAllowedCarLocal(ry.car_number));

      // 2) expenses (이번달 / 전년 동월)
      // ✅ expenses 즉시 필터
      const Ef  = (E  || []).filter(e  => isAllowedCarLocal(e.car_number));
      const EYf = (EY || []).filter(ey => isAllowedCarLocal(ey.car_number));

      // 3) safety (이번달만) — 차량번호 기준 제외 조건이 없다면 그대로 사용
      // ✅ 필터된 데이터로 상태 세팅
      setReservations(Rf);
      setReservationsPrev(Rpf);
      setReservationsYearAgo(Ryf);
      setExpenses(Ef);
      setExpensesPrevYear(EYf);
      setSafety(S || []);

    } catch (err) {
      if (!current()) return;
      console.error(err);
      showUserAlert("월말 결산 조회 실패: " + (err.message || err));
      setReservations([]); setReservationsPrev([]); setReservationsYearAgo([]);
      setExpenses([]); setExpensesPrevYear([]); setSafety([]);
    } finally {
      if (current()) setLoading(false);
    }
  }, [company, month]);


  useEffect(() => {
    fetchAll();
    return () => { loadGeneration.current += 1; };
  }, [fetchAll]);

  // ----------------- 집계 로직 -----------------
  // 총 매출 & 전월 대비
  const totalSales = useMemo(
    () => reservationsF.reduce((a, r) => a + (Number(r.money) || 0), 0),
    [reservationsF]
  );
  const prevSales = useMemo(
    () => reservationsPrevF.reduce((a, r) => a + (Number(r.money) || 0), 0),
    [reservationsPrevF]
  );
  const mom = useMemo(()=> {
    if (!prevSales) return null; // 전월 0일 때는 % 표시 X
    return ((totalSales - prevSales) / prevSales) * 100;
  }, [totalSales, prevSales]);

  // 차량별 매출 & 가동률(해당 월 유니크 근무일수 / 그 달 총일수)
  const { carSales, carUtil } = useMemo(()=> {
    const sales = {};
    const days = {};
    for (const r of reservationsF) {
      const car = r.car_number || "(미지정)";
      sales[car] = (sales[car]||0) + (Number(r.money)||0);
      if (!days[car]) days[car] = new Set();
      if (r.date) days[car].add(r.date);
    }
    const { daysInMonth } = monthBounds(month);
    const util = Object.fromEntries(Object.keys(days).map(car => {
      const worked = days[car].size;
      return [car, { workedDays: worked, utilRate: (worked / daysInMonth) }];
    }));
    return { carSales: sales, carUtil: util };
  }, [reservationsF, month]);

  // 주간 매출(월 안에서 주차 label)
  const weeklyTrend = useMemo(()=> {
    const { from } = monthBounds(month);
    const start = new Date(from + "T00:00:00Z");
    const weekKey = (dStr) => {
      const d = new Date(dStr + "T00:00:00Z");
      // 월 내 주차 번호 (1주차부터)
      const dayOffset = Math.floor((d - start)/86400000);
      const wk = Math.floor(dayOffset/7) + 1;
      return `${wk}주`;
    };
    const byWeek = {};
    for (const r of reservationsF) {
      if (!r.date) continue;
      const k = weekKey(r.date);
      byWeek[k] = (byWeek[k]||0) + (Number(r.money)||0);
    }
    return Object.entries(byWeek).sort((a,b)=> {
      const na = Number(a[0].replace("주",""));
      const nb = Number(b[0].replace("주",""));
      return na-nb;
    }).map(([week, amount])=> ({ week, amount }));
  }, [reservationsF, month]);

  // 주간 집계(올해/작년) + 병합도 F 버전 사용
  const weeklyThisYear   = useMemo(() => weeklyAggregate(reservationsF,        month),           [reservationsF, month]);
  const weeklyLastYear   = useMemo(() => weeklyAggregate(reservationsYearAgoF, yearAgoStr(month)), [reservationsYearAgoF, month]);

  // 차트용 병합 (주 라벨 기준 유니온)
  const weeklyMerged = useMemo(() => {
    const map = new Map();
    for (const d of weeklyThisYear) {
      map.set(d.week, { week: d.week, thisYear: d.amount, lastYear: 0 });
    }
    for (const d of weeklyLastYear) {
      if (!map.has(d.week)) map.set(d.week, { week: d.week, thisYear: 0, lastYear: d.amount });
      else map.get(d.week).lastYear = d.amount;
    }
    return Array.from(map.values())
      .sort((a,b) => Number(a.week.replace("주","")) - Number(b.week.replace("주","")));
  }, [weeklyThisYear, weeklyLastYear]);

  

  // 차량 가동률 랭킹 (최다/최저) — 4자리 차량번호만 집계
  const utilRanking = useMemo(() => {
    const arr = Object.entries(carUtil)
      .filter(([car]) => isFourDigitCar(car))
      .map(([car, v]) => ({
        car,
        workedDays: v.workedDays || 0,
        utilRate: v.utilRate || 0,
      }));

    // workedDays 내림차순, 동률이면 utilRate, 그 다음 사전순
    arr.sort(
      (a, b) =>
        b.workedDays - a.workedDays ||
        b.utilRate - a.utilRate ||
        String(a.car).localeCompare(String(b.car))
    );
    return arr;
  }, [carUtil]);

  const hasRank = utilRanking.length > 0;
  const best = hasRank ? utilRanking[0] : null;
  const worst = hasRank ? utilRanking[utilRanking.length - 1] : null;

  const { daysInMonth } = monthBounds(month);

  const sumPie = (arr) => arr.reduce((a, x) => a + (Number(x.value) || 0), 0);
  const totalPieThis  = useMemo(() => sumPie(pieThisMonth),  [pieThisMonth]);
  const totalPieYear  = useMemo(() => sumPie(pieYearAgo),    [pieYearAgo]);

  
  const moneyPctFormatter = (total) => (value, name) => {
    const v = Number(value) || 0;
    const pct = total ? (v / total) * 100 : 0;
    return [`${v.toLocaleString()}원 (${pct.toFixed(1)}%)`, name];
  };


  // 차량별 월 직접비(유류/통행료/인건비/차량가) 합계
  const carDirectCost = useMemo(() => {
    const sumKeys = (row, keys)=> keys.reduce((acc,k)=> acc + (Number(row[k])||0), 0);
    const out = {};
    for (const e of expensesF) {
      const car = e.car_number || "(미지정)";
      if (!out[car]) out[car] = { fuel:0, tolls:0, labor:0, vehicle:0, total:0 };
      const add = {
        fuel:  sumKeys(e, EXPENSE.fuel),
        tolls: sumKeys(e, EXPENSE.tolls),
        labor: sumKeys(e, EXPENSE.labor),
        vehicle: sumKeys(e, EXPENSE.vehicle),
      };
      out[car].fuel    += add.fuel;
      out[car].tolls   += add.tolls;
      out[car].labor   += add.labor;
      out[car].vehicle += add.vehicle;
      out[car].total   += (add.fuel+add.tolls+add.labor+add.vehicle);
    }
    return out;
  }, [expensesF]);

  // 🔎 노선별 손익표(간단한 배분 로직)
  //  - 같은 차량의 월 ‘직접비’를 그 차량이 그 달에 번 ‘노선별 매출’ 비율로 배분
  const routePnL = useMemo(() => {
    const carRouteSales = {};
    const carTotalSales = {};
    for (const r of reservationsF) {
      const car = r.car_number || "(미지정)";
      const rk = `${r.start||""}→${r.destination||""}`;
      const amt = Number(r.money) || 0;
      if (!carRouteSales[car]) carRouteSales[car] = {};
      carRouteSales[car][rk] = (carRouteSales[car][rk] || 0) + amt;
      carTotalSales[car] = (carTotalSales[car] || 0) + amt;
    }
    const agg = {};
    const ensure = (rk) => (agg[rk] ||= { sales:0, fuel:0, tolls:0, labor:0, gross:0, carSet:new Set() });
    for (const car of Object.keys(carRouteSales)) {
      const totalS = carTotalSales[car] || 0;
      const dc = carDirectCost[car] || { fuel:0, tolls:0, labor:0, vehicle:0, total:0 };
      for (const rk of Object.keys(carRouteSales[car])) {
        const s = carRouteSales[car][rk];
        const ratio = totalS ? (s/totalS) : 0;
        const e = ensure(rk);
        e.sales += s;
        e.fuel  += dc.fuel  * ratio;
        e.tolls += dc.tolls * ratio;
        e.labor += dc.labor * ratio;
        e.carSet.add(car);
      }
    }
    for (const rk of Object.keys(agg)) {
      const a = agg[rk];
      a.gross = a.sales - (a.fuel + a.tolls + a.labor);
    }
    return Object.entries(agg).map(([route, v]) => ({
      route,
      sales:v.sales, fuel:v.fuel, tolls:v.tolls, labor:v.labor, gross:v.gross,
      carNumbers: Array.from(v.carSet),
    })).sort((a,b)=> b.sales - a.sales);
  }, [reservationsF, carDirectCost]);


  // 지연 사유 상위: safety.summary 토큰에서 키워드 카운트
  const delayTop = useMemo(()=> {
    const keys = [
      { key: "차량 지연",  match: ["차량 지연","차량지연","버스 지연","버스지연"] },
      { key: "승객 지연",  match: ["승객 지연","승객지연","탑승 지연","탑승지연"] },
      { key: "도로 정체",  match: ["도로 정체","정체","교통 혼잡","혼잡"] },
    ];
    const cnt = Object.fromEntries(keys.map(k=> [k.key, 0]));
    for (const s of safety) {
      const txt = (s.summary || s.etc || "").toString().toLowerCase();
      for (const k of keys) {
        if (k.match.some(m => txt.includes(m.toLowerCase()))) cnt[k.key] += 1;
      }
    }
    // 상위 3
    return Object.entries(cnt).sort((a,b)=> b[1]-a[1]).slice(0,3)
      .map(([reason, count]) => ({ reason, count }));
  }, [safety]);

  // 안전/컴플라이언스: 사고/과태료/민원 건수
  const safetyBlock = useMemo(()=> {
    const box = { 사고:0, 과태료:0, 민원:0 };
    for (const s of safety) {
      const t = (s.issue || "").trim();
      if (box[t] !== undefined) box[t] += 1;
    }
    return box;
  }, [safety]);

  // 비용 요약(월 합계)
  const costSummary = useMemo(() => {
    const sumKeys = (rows, keys) => rows.reduce((acc,row)=> acc + keys.reduce((a,k)=> a+(Number(row[k])||0), 0), 0);
    const fuel    = sumKeys(expensesF, EXPENSE.fuel);
    const labor   = sumKeys(expensesF, EXPENSE.labor);
    const tolls   = sumKeys(expensesF, EXPENSE.tolls);
    const vehicle = sumKeys(expensesF, EXPENSE.vehicle);
    return { fuel, labor, tolls, vehicle };
  }, [expensesF]);

  // ----------------- AI 요약 (선택) -----------------
  const [aiMsg, setAiMsg] = useState("");  
  const [aiLoading, setAiLoading] = useState(false);
  const buildAISummaryInput = () => {
    return {
      month,
      totalSales,
      momPercent: mom,
      carSales,
      carUtil, // {car: {workedDays, utilRate}}
      weeklyTrend,
      routePnL, // [{route, sales, fuel, tolls, labor, gross}]
      delayTop, // [{reason, count}]
      safetyBlock, // {사고, 과태료, 민원}
      costSummary, // {fuel, labor, tolls, vehicle}
    };
  };
  const handleAskAI = async () => {
    if (aiLocked) {                           // ✅ 추가 가드
      showUserAlert("이 달은 이미 AI 요약을 생성했습니다.");
      return;
    }
    try {
        setAiLoading(true);
        const payload = buildAISummaryInput();
        // 🔥 Edge Function 이름: closing-ai
        const { data, error } = await supabase.functions.invoke("closing-ai", {
        body: payload,
        });
        if (error) throw error;
        setAiMsg(data?.summary ?? "(응답이 비어있습니다)");
         // ✅ 생성 직후 DB 저장(있으면 갱신)
         await saveAiSummary(data?.summary);
    } catch (e) {
        showUserAlert("AI 요약 실패: " + (e.message || e));
    } finally {
        setAiLoading(false);
    }
  };

  // ----------------- UI -----------------
  const { from, toIncl } = monthBounds(month);

  // 운행 요약: 운행 건수, 평균 단가, 차량별 매출 랭킹
  const tripStats = useMemo(() => {
    const count = reservationsF.length;
    const avgFare = count ? Math.round(totalSales / count) : 0;

    const perCar = {};
    for (const r of reservationsF) {
      const car = r.car_number || "(미지정)";
      perCar[car] = (perCar[car] || 0) + (Number(r.money) || 0);
    }
    const ranking = Object.entries(perCar)
      .map(([car, amt]) => ({ car, amt }))
      .sort((a, b) => b.amt - a.amt);

    return { count, avgFare, ranking };
  }, [reservationsF, totalSales]);

  const topCars = useMemo(() => tripStats.ranking.slice(0, 5), [tripStats]);
  const bottomCars = useMemo(
    () => tripStats.ranking.slice(-5).reverse(),
    [tripStats]
  );

  // 요일별 매출/건수
  const dowAgg = useMemo(() => {
    const sales = [0,0,0,0,0,0,0];
    const trips = [0,0,0,0,0,0,0];
    for (const r of reservationsF) {
      if (!r.date) continue;
      const d = new Date(r.date + "T00:00:00Z");
      const w = d.getUTCDay(); // 0:일 ~ 6:토
      const m = Number(r.money) || 0;
      sales[w] += m;
      trips[w] += 1;
    }
    const labels = ["일","월","화","수","목","금","토"];
    return labels.map((label, i) => ({ label, sales: sales[i], trips: trips[i] }));
  }, [reservationsF]);

  // 상위 경로(출발→도착) Top 10
  const topRoutes = useMemo(() => {
    const map = {};
    for (const r of reservationsF) {
      const key = `${r.start || ""}→${r.destination || ""}`;
      map[key] = (map[key] || 0) + (Number(r.money) || 0);
    }
    return Object.entries(map)
      .map(([route, amt]) => ({ route, amt }))
      .sort((a, b) => b.amt - a.amt)
      .slice(0, 10);
  }, [reservationsF]);

  return (
    <div className="space-y-4">
      <Header />
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">월말 결산</h2>
          <div className="flex gap-2">
            <Input type="month" value={month} onChange={e=> setMonth(e.target.value)} className="w-40" />
            <Button onClick={fetchAll} disabled={loading}>{loading?"조회중...":"새로고침"}</Button>
          </div>
        </div>

        <div className="rounded border bg-white p-4 shadow-md space-y-4">
        <Card className="border-0 shadow-none"><CardContent className="bg-white p-4 sm:p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-gray-500">기간</div>
              <div className="font-semibold">{from} ~ {toIncl}</div>
            </div>
            <div>
              <div className="text-gray-500">총 매출</div>
              <div className="font-semibold">
                {number(totalSales)}원 {mom!=null && (
                  <span className={mom>=0 ? "text-emerald-600" : "text-rose-600"}>
                    ({mom>=0?"+":""}{mom.toFixed(1)}%)
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500">차량별: {Object.keys(carSales).length}대</div>
            </div>
            <div>
              <div className="text-gray-500">차량 가동률(평균)</div>
              <div className="font-semibold">
                {(()=>{
                  const vals = Object.values(carUtil).map(v=>v.utilRate);
                  const avg = vals.length ? (vals.reduce((a,b)=>a+b,0)/vals.length) : 0;
                  return (avg*100).toFixed(1) + "%";
                })()}
              </div>
            </div>
          </div>
        </CardContent></Card>

        {/* 주간 막대 그래프 */}
        <Card className="border-0 shadow-none"><CardContent className="bg-white p-4 sm:p-5">
          <div className="font-semibold mb-2">매출 추이(주간)</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={weeklyMerged} reverseStackOrder>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis
                tickFormatter={(v) =>
                  `${(v / 1_000_000).toLocaleString("ko-KR", {
                    maximumFractionDigits: 1,
                    minimumFractionDigits: 0,
                  })}백만`
                }
              />
              <Tooltip formatter={(v)=> (Number(v)/1_000_000).toLocaleString()+"백만원"} />
              {/* 작년 동월 존재할 때만 비교 막대 추가 */}
              <Bar dataKey="lastYear" name={`${yearAgoStr(month)} (작년)`} fill="#94a3b8"   
                fillOpacity={0.8}       // 살짝 투명
                stroke="#64748b"        // 테두리(선택)
                strokeWidth={1}/>
              {/* 올해 */}
              <Bar dataKey="thisYear" name={`${month} (올해)`} fill="#3b82f6"/>
            </BarChart>
          </ResponsiveContainer>
        </CardContent></Card>


        {/* 비용 구조 (원형 그래프: 전년 동월 / 이번달) */}
        <Card className="border-0 shadow-none">
          <CardContent className="bg-white p-4 sm:p-5">
            <div className="font-semibold mb-2">
              비용 구조 — 전년 동월 vs 이번달
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 전년 동월 */}
              <div>
                <div className="text-sm text-gray-600 mb-1">
                  {yearAgoStr(month)} 비용 구조
                </div>
                {pieYearAgo.length > 0 ? (
                  <div style={{ width: "100%", height: 300 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Tooltip formatter={moneyPctFormatter(totalPieYear)} />
                        <Legend />
                        <Pie
                          data={pieYearAgo}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={60}
                          outerRadius={100}
                          isAnimationActive={false}
                        >
                          {pieYearAgo.map((d) => (
                            <Cell key={d.name} fill={colorFor(d.name)} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-400 rounded">
                    데이터 없음
                  </div>
                )}
              </div>

              {/* 이번달 */}
              <div>
                <div className="text-sm text-gray-600 mb-1">
                  {month} 비용 구조
                </div>
                <div style={{ width: "100%", height: 300 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Tooltip formatter={moneyPctFormatter(totalPieThis)} />
                      <Legend />
                      <Pie
                        data={pieThisMonth}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={60}
                        outerRadius={100}
                        isAnimationActive={false}
                      >
                      {pieThisMonth.map((d) => (
                          <Cell key={d.name} fill={colorFor(d.name)} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>


        {/* 노선별 손익표 */}
        {/*<Card><CardContent className="bg-white">
          <div className="font-semibold mb-2">노선별 손익표 (매출-직접비: 유류/통행료/인건비)</div>
          <div className="overflow-x-auto">
            <table className="table-auto w-full border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border p-2 text-left">노선</th>
                  <th className="border p-2 text-right">매출</th>
                  <th className="border p-2 text-right">유류</th>
                  <th className="border p-2 text-right">통행료</th>
                  <th className="border p-2 text-right">인건비</th>
                  <th className="border p-2 text-right">손익</th>
                </tr>
              </thead>
              <tbody>
                {routePnL.map((r, i)=>(
                  <tr key={i}>
                    <td className="border p-2">
                        {r.carNumbers?.length > 0 && (
                            <div className="text-xs text-gray-500 mb-0.5">[{r.carNumbers.join(", ")}]</div>
                        )}
                        <div>{r.route}</div>
                    </td>
                    <td className="border p-2 text-right">{number(r.sales)}</td>
                    <td className="border p-2 text-right">{number(r.fuel)}</td>
                    <td className="border p-2 text-right">{number(r.tolls)}</td>
                    <td className="border p-2 text-right">{number(r.labor)}</td>
                    <td className={`border p-2 text-right ${r.gross>=0?"text-emerald-700":"text-rose-700"}`}>{number(r.gross)}</td>
                  </tr>
                ))}
                {routePnL.length===0 && <tr><td colSpan={6} className="border p-2 text-center text-gray-500">데이터 없음</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent></Card>*/}

        {/* 운행 요약 & 차량 매출 랭킹 */}
        <Card className="border-0 shadow-none">
          <CardContent className="bg-white p-4 sm:p-5">
            <div className="font-semibold mb-2">운행 요약 & 차량 랭킹</div>

            {/* KPI 요약 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mb-4">
              <div className="p-4 rounded-lg border">
                <div className="text-gray-500 mb-1">이번달 운행 건수</div>
                <div className="font-semibold">{tripStats.count.toLocaleString()} 건</div>
              </div>
              <div className="p-4 rounded-lg border">
                <div className="text-gray-500 mb-1">평균 단가(운행당)</div>
                <div className="font-semibold">{number(tripStats.avgFare)} 원</div>
              </div>
              <div className="p-4 rounded-lg border">
                <div className="text-gray-500 mb-1">총 매출(필터 반영)</div>
                <div className="font-semibold">{number(totalSales)} 원</div>
              </div>
            </div>

            {/* Top/Bottom 차량 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-600 mb-1">차량 매출 Top 5</div>
                <div className="overflow-x-auto">
                  <table className="table-auto w-full border">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border p-2 text-left">차량</th>
                        <th className="border p-2 text-right">매출</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topCars.map((c) => (
                        <tr key={`top-${c.car}`}>
                          <td className="border p-2">{c.car}</td>
                          <td className="border p-2 text-right">{number(c.amt)}</td>
                        </tr>
                      ))}
                      {topCars.length === 0 && (
                        <tr>
                          <td className="border p-2 text-center text-gray-500" colSpan={2}>
                            데이터 없음
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div className="text-gray-600 mb-1">차량 매출 Bottom 5</div>
                <div className="overflow-x-auto">
                  <table className="table-auto w-full border">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border p-2 text-left">차량</th>
                        <th className="border p-2 text-right">매출</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bottomCars.map((c) => (
                        <tr key={`bottom-${c.car}`}>
                          <td className="border p-2">{c.car}</td>
                          <td className="border p-2 text-right">{number(c.amt)}</td>
                        </tr>
                      ))}
                      {bottomCars.length === 0 && (
                        <tr>
                          <td className="border p-2 text-center text-gray-500" colSpan={2}>
                            데이터 없음
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 요일별 패턴 & 상위 경로 */}
        <Card className="border-0 shadow-none">
          <CardContent className="bg-white p-4 sm:p-5">
            <div className="font-semibold mb-2">요일별 패턴 & 상위 경로</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 요일별 매출/건수 차트 */}
              <div>
                <div className="text-sm text-gray-600 mb-1">요일별 매출/건수</div>
                <div style={{ width: "100%", height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={dowAgg}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" />
                      {/* 매출 축 (왼쪽) */}
                      <YAxis
                        yAxisId="left"
                        tickFormatter={(v) =>
                          `${(v / 1_000_000).toLocaleString("ko-KR", {
                            maximumFractionDigits: 1,
                            minimumFractionDigits: 0,
                          })}백만`
                        }
                      />
                      {/* 건수 축 (오른쪽) */}
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip
                        formatter={(v, name) =>
                          name === "매출"
                            ? `${(Number(v) / 1_000_000).toLocaleString()}백만원`
                            : `${v}건`
                        }
                      />
                      <Legend />
                      <Bar yAxisId="left"  dataKey="sales" name="매출" fill="#3b82f6" /* blue-500 */ />
                      <Bar yAxisId="right" dataKey="trips" name="건수" fill="#94a3b8" /* slate-400 */ />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* 상위 경로 Top 10 */}
              <div>
                <div className="text-sm text-gray-600 mb-1">상위 경로 Top 10</div>
                <div className="overflow-x-auto">
                  <table className="table-auto w-full border text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border p-2 text-left">경로</th>
                        <th className="border p-2 text-right">매출</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topRoutes.map((r) => (
                        <tr key={r.route}>
                          <td className="border p-2">{r.route}</td>
                          <td className="border p-2 text-right">{number(r.amt)}</td>
                        </tr>
                      ))}
                      {topRoutes.length === 0 && (
                        <tr>
                          <td className="border p-2 text-center text-gray-500" colSpan={2}>
                            데이터 없음
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>


        {/* 안전/지연 & 비용 요약 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-0 shadow-none"><CardContent className="bg-white p-4 sm:p-5">
            <div className="font-semibold mb-2">지연 사유 상위</div>
            <ul className="list-disc pl-5 text-sm">
              {delayTop.map((d,i)=><li key={i}>{d.reason}: {d.count}건</li>)}
              {delayTop.length===0 && <li className="text-gray-500">데이터 없음</li>}
            </ul>
            <div className="font-semibold mt-4 mb-1">안전/컴플라이언스</div>
            <div className="text-sm">사고: {safetyBlock.사고||0}건 / 과태료: {safetyBlock.과태료||0}건 / 민원: {safetyBlock.민원||0}건</div>
          </CardContent></Card>

          <Card className="border-0 shadow-none">
            <CardContent className="bg-white p-4 sm:p-5">
              <div className="font-semibold mb-2">차량 가동률 상세</div>

              {!hasRank ? (
                <div className="text-sm text-gray-500">
                  데이터 없음 (4자리 차량번호에 해당하는 운행 데이터가 없습니다)
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-4 rounded-lg border">
                      <div className="text-gray-500 mb-1">이번달 최다 운행</div>
                      <div className="font-semibold">{best.car}</div>
                      <div>
                        운행일수: <b>{best.workedDays}</b>일 / 가동률:{" "}
                        <b>{(best.utilRate * 100).toFixed(1)}%</b> (총 {daysInMonth}일 중)
                      </div>
                    </div>
                    <div className="p-4 rounded-lg border">
                      <div className="text-gray-500 mb-1">이번달 최저 운행</div>
                      <div className="font-semibold">{worst.car}</div>
                      <div>
                        운행일수: <b>{worst.workedDays}</b>일 / 가동률:{" "}
                        <b>{(worst.utilRate * 100).toFixed(1)}%</b> (총 {daysInMonth}일 중)
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="text-gray-500 mb-1">Top 3</div>
                      <ul className="list-disc pl-5">
                        {utilRanking.slice(0, 3).map((r) => (
                          <li key={`top-${r.car}`}>
                            {r.car} — {r.workedDays}일 ({(r.utilRate * 100).toFixed(1)}%)
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="text-gray-500 mb-1">Bottom 3</div>
                      <ul className="list-disc pl-5">
                        {utilRanking.slice(-3).map((r) => (
                          <li key={`bottom-${r.car}`}>
                            {r.car} — {r.workedDays}일 ({(r.utilRate * 100).toFixed(1)}%)
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

        </div>

        {/* AI 요약 */}
        <Card className="border-0 shadow-none">
          <CardContent className="bg-white p-4 sm:p-5">
            <div className="flex items-center justify-between">
              <div className="font-semibold">AI 요약</div>
              <Button onClick={handleAskAI} disabled={aiLoading || aiLocked}>
                {aiLocked ? "이번 달 요약 완료" : (aiLoading ? "요약중..." : "AI에게 요약 요청")}
              </Button>
            </div>
            {aiLocked && <div className="text-xs text-gray-500 mt-1">* 이 달에는 한 번만 생성할 수 있어요.</div>}
            <div className="mt-2 whitespace-pre-wrap text-sm">{renderAiSummary(aiMsg)}</div>
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
}
